import React, { useEffect, useState } from 'react';
import cohortAnalyticsService, {
  CohortGrouping,
  CohortSummary,
  HeatmapData,
  RetentionCurveResponse,
  formatPercentage,
  formatCurrency
} from '../services/cohortAnalyticsService';
import CohortRetentionHeatmap from './CohortRetentionHeatmap';
import RetentionCurveChart from './RetentionCurveChart';

interface DashboardState {
  loading: boolean;
  error?: string;
  summary?: CohortSummary;
  heatmapData?: HeatmapData;
  retentionCurve?: RetentionCurveResponse;
  grouping: CohortGrouping;
  maxPeriods: number;
}

const CohortAnalyticsDashboard: React.FC = () => {
  const [state, setState] = useState<DashboardState>({
    loading: true,
    grouping: 'monthly',
    maxPeriods: 12
  });

  async function loadData(grouping: CohortGrouping, maxPeriods: number) {
    setState(s => ({ ...s, loading: true, error: undefined }));
    try {
      const [summaryData, heatmapData, retentionCurve] = await Promise.all([
        cohortAnalyticsService.getCohortSummary(),
        cohortAnalyticsService.getHeatmapData({ grouping, maxPeriods }),
        cohortAnalyticsService.getRetentionCurve({ grouping, maxPeriods })
      ]);

      setState(s => ({
        ...s,
        loading: false,
        summary: summaryData,
        heatmapData,
        retentionCurve,
        grouping,
        maxPeriods
      }));
    } catch (err: any) {
      setState(s => ({
        ...s,
        loading: false,
        error: err.message || 'Failed to load cohort analytics'
      }));
    }
  }

  useEffect(() => {
    loadData(state.grouping, state.maxPeriods);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGroupingChange = (newGrouping: CohortGrouping) => {
    loadData(newGrouping, state.maxPeriods);
  };

  const handlePeriodsChange = (newPeriods: number) => {
    loadData(state.grouping, newPeriods);
  };

  const { loading, error, summary, heatmapData, retentionCurve, grouping, maxPeriods } = state;

  return (
    <div className="space-y-8 p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-2xl font-bold">Cohort Analysis</h1>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Grouping:</label>
            <select
              value={grouping}
              onChange={e => handleGroupingChange(e.target.value as CohortGrouping)}
              className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Periods:</label>
            <select
              value={maxPeriods}
              onChange={e => handlePeriodsChange(parseInt(e.target.value, 10))}
              className="px-3 py-1.5 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-sm"
            >
              {[6, 12, 18, 24].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="p-3 rounded bg-red-50 dark:bg-red-900/30 border border-red-300 dark:border-red-800 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading cohort data...</div>
        </div>
      )}

      {/* Summary Cards */}
      {!loading && summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <SummaryCard
            title="Total Cohorts"
            value={summary.totalCohorts.toString()}
          />
          <SummaryCard
            title="Month 1 Retention"
            value={formatPercentage(summary.avgRetentionMonth1)}
            subtitle="Average retention"
          />
          <SummaryCard
            title="Month 3 Retention"
            value={formatPercentage(summary.avgRetentionMonth3)}
            subtitle="Average retention"
          />
          <SummaryCard
            title="Month 6 Retention"
            value={formatPercentage(summary.avgRetentionMonth6)}
            subtitle="Average retention"
          />
          <SummaryCard
            title="Avg Churn Rate"
            value={formatPercentage(summary.avgChurnRate)}
            subtitle="Monthly churn"
            valueColor="text-red-600 dark:text-red-400"
          />
        </div>
      )}

      {/* Lifetime Value and Best/Worst Cohorts */}
      {!loading && summary && (
        <div className="grid md:grid-cols-3 gap-4">
          <DetailCard
            label="Avg Lifetime Value"
            value={formatCurrency(summary.avgLifetimeValue)}
            description="Average LTV per patron"
          />
          <DetailCard
            label="Best Performing Cohort"
            value={summary.bestPerformingCohort.date}
            description={`${formatPercentage(summary.bestPerformingCohort.retentionRate)} retention`}
            valueColor="text-green-600 dark:text-green-400"
          />
          <DetailCard
            label="Worst Performing Cohort"
            value={summary.worstPerformingCohort.date}
            description={`${formatPercentage(summary.worstPerformingCohort.retentionRate)} retention`}
            valueColor="text-red-600 dark:text-red-400"
          />
        </div>
      )}

      {/* Retention Curve Chart */}
      {!loading && retentionCurve && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">Average Retention Curve</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Shows average retention rate across all cohorts by period
            </p>
          </div>
          <RetentionCurveChart data={retentionCurve} />
        </section>
      )}

      {/* Retention Heatmap */}
      {!loading && heatmapData && (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold">Retention Heatmap</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Visualize retention rates by cohort and period (darker = higher retention)
            </p>
          </div>
          <CohortRetentionHeatmap heatmapData={heatmapData} />
        </section>
      )}

      {/* Empty State */}
      {!loading && (!summary || summary.totalCohorts === 0) && (
        <div className="text-center py-12 text-gray-500">
          <p>No cohort data available.</p>
          <p className="text-sm mt-2">Create memberships to generate cohort analytics.</p>
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------------
// Sub-components
// ----------------------------------------------------------------------------------

interface SummaryCardProps {
  title: string;
  value: string;
  subtitle?: string;
  valueColor?: string;
}

function SummaryCard({ title, value, subtitle, valueColor = 'text-gray-900 dark:text-white' }: SummaryCardProps) {
  return (
    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
        {title}
      </div>
      <div className={`text-2xl font-bold ${valueColor}`}>
        {value}
      </div>
      {subtitle && (
        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {subtitle}
        </div>
      )}
    </div>
  );
}

interface DetailCardProps {
  label: string;
  value: string;
  description?: string;
  valueColor?: string;
}

function DetailCard({ label, value, description, valueColor = 'text-gray-900 dark:text-white' }: DetailCardProps) {
  return (
    <div className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
        {label}
      </div>
      <div className={`text-lg font-semibold ${valueColor}`}>
        {value}
      </div>
      {description && (
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {description}
        </div>
      )}
    </div>
  );
}

export default CohortAnalyticsDashboard;

